import React, { useEffect, useState } from 'react';

import FilterActions, {
  cloneFilterInputActions,
  cloneFilterActions,
} from '../FilterActions';
import {
  useFiltersFields,
  useFiltersFieldsManagerContext,
} from '../FilterFieldsManager';
import { useFiltersSettings, FILTERS_MODE_PRIMARY } from '../FiltersSettings';
import {
  isEqualFilterValues,
  useFiltersStateManager,
} from '../FiltersStateManager';
import FilterField from './FilterField';
import LayoutFilterGeneral from './components/LayoutFilterGeneral';
import LayoutFilterPrimary from './components/LayoutFilterPrimary';
import { useFiltersRequiredManager } from '../FiltersRequiredManager/useFiltersRequiredManager';
import getTitle from './getTitle';

const Filter = ({
  title,
  name,
  source,
  sourceLimit,
  depends,
  normalizeValue,
  withClear,
  withFavoriteActions,
  actions = [],
  compareActions = [],
  lazy = true,
  suffix,
  disabled,
  disableEmpty,
  renderComponent,
  multiFields = true,
  serverSideSearch = false,
  required = false,
}) => {
  const { mode, isFilterVisible } = useFiltersSettings();

  const { getFilterEmptyValue, getFilterValue } = useFiltersStateManager();
  const { compared } = useFiltersFieldsManagerContext();

  const { fields } = useFiltersFields({ name, depends });

  const [error, onError] = useState();
  const { registerRequired, unregisterRequired } = useFiltersRequiredManager();

  useEffect(() => {
    if (required) {
      fields.forEach(({ path }) => {
        registerRequired(path);
      });
    }
    return () => {
      fields.forEach(({ path }) => {
        unregisterRequired(path);
      });
    };
  }, [required, name]);

  if (!isFilterVisible({ name })) {
    return false;
  }

  if (mode === FILTERS_MODE_PRIMARY) {
    const canClear =
      withClear &&
      fields.some(
        ({ path }) =>
          !isEqualFilterValues(getFilterEmptyValue(path), getFilterValue(path))
      );

    return (
      <LayoutFilterPrimary compared={compared} error={error}>
        {fields.map(({ path, basePath, dependsFields, getPathByName }, i) => {
          if (!multiFields && i) {
            return false;
          }

          return (
            <FilterField
              key={`${fields.length}_${path}`}
              label={!i ? getTitle(title, required) : null}
              name={name}
              path={path}
              source={source}
              sourceLimit={sourceLimit}
              onError={onError}
              dependsFields={dependsFields}
              getPathByName={getPathByName}
              renderComponent={renderComponent}
              normalizeValue={normalizeValue}
              multiFields={multiFields}
              lazy={lazy}
              suffix={suffix}
              disabled={disabled}
              disableEmpty={disableEmpty}
              serverSideSearch={serverSideSearch}
              actions={cloneFilterInputActions(
                !i
                  ? [
                      ...actions,
                      canClear ? <FilterActions.ClearPrimaryFilter /> : null,
                    ]
                  : compareActions,
                {
                  name,
                  path,
                  basePath,
                  getPathByName,
                }
              )}
            />
          );
        })}
      </LayoutFilterPrimary>
    );
  }

  const { path, basePath, getPathByName } = fields[fields.length - 1];

  return (
    <LayoutFilterGeneral
      label={getTitle(title, required)}
      compared={compared}
      error={error}
      actions={cloneFilterActions(
        [
          ...(fields.length > 1 ? compareActions : []),
          ...actions,
          withFavoriteActions ? <FilterActions.Favorite /> : null,
        ],
        {
          name,
          path,
          basePath,
          getPathByName,
        }
      )}
    >
      {fields.map(({ path, dependsFields, getPathByName }, i) => {
        if (!multiFields && i) {
          return false;
        }

        return (
          <FilterField
            key={`${fields.length}_${path}`}
            name={name}
            path={path}
            source={source}
            sourceLimit={sourceLimit}
            onError={onError}
            dependsFields={dependsFields}
            getPathByName={getPathByName}
            normalizeValue={normalizeValue}
            renderComponent={renderComponent}
            multiFields={multiFields}
            lazy={lazy}
            suffix={suffix}
            disabled={disabled}
            disableEmpty={disableEmpty}
            serverSideSearch={serverSideSearch}
          />
        );
      })}
    </LayoutFilterGeneral>
  );
};

export default Filter;
